import React, { useState } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import tw from "twrnc";

import { colors, radius, text } from "@/components/settings/theme";
import { t, tn } from "@/localization";
import { openWorkspace, singleSharedSlug } from "./openWorkspace";
import RolePill from "./RolePill";
import useWorkspace from "./useWorkspace";

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

export const AccountAvatar = ({ entry, size = 36 }) =>
  entry?.logo_path ? (
    <Image
      source={{ uri: entry.logo_path }}
      resizeMode="contain"
      style={{
        width: size,
        height: size,
        borderRadius: radius.control,
        backgroundColor: colors.white,
        borderWidth: 1,
        borderColor: colors.divider,
      }}
    />
  ) : (
    <View
      style={[
        tw`items-center justify-center`,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: colors.surfaceStrong,
        },
      ]}
    >
      <Text style={[text.bodyStrong, { color: colors.slate }]}>
        {initials(entry?.name)}
      </Text>
    </View>
  );

// What an entry covers, in a few words.
export const coverageText = (entry) => {
  if (entry.accountRole) return t("All events");
  if (entry.events.length === 1) return entry.events[0].title || t("One event");
  return tn("{count} event", "{count} events", entry.events.length);
};

// Your event list's "Shared with you" strip (PLAN.md §5.5): the accounts and
// events other people shared with you. A card opens that account, or the one
// event shared from it.
const SharedWithYou = ({ navigation, style }) => {
  const ws = useWorkspace();
  const [opening, setOpening] = useState(null);
  if (!ws.enabled || ws.isShared || !ws.accounts.length) return null;

  const open = async (entry) => {
    if (opening) return;
    setOpening(entry.owner);
    try {
      await openWorkspace(navigation, entry.owner, {
        slug: singleSharedSlug(entry),
        source: "shared_strip",
      });
    } finally {
      setOpening(null);
    }
  };

  return (
    <View style={style}>
      <Text style={[text.label, tw`mb-2`]}>{t("Shared with you")}</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={tw`gap-3 pr-2`}
      >
        {ws.accounts.map((entry) => (
          <Pressable
            key={entry.owner}
            onPress={() => open(entry)}
            accessibilityRole="button"
            accessibilityLabel={
              entry.accountRole
                ? t("Open {team}'s account", { team: entry.name })
                : t("Open {team}'s event", { team: entry.name })
            }
            style={({ hovered, pressed }) => [
              tw`flex-row items-center px-3 py-2.5`,
              {
                width: 260,
                borderRadius: radius.card,
                borderWidth: 1,
                borderColor: colors.divider,
                backgroundColor:
                  hovered || pressed ? colors.surfaceStrong : colors.surface,
              },
            ]}
          >
            <AccountAvatar entry={entry} />
            <View style={tw`flex-1 ml-3`}>
              <Text numberOfLines={1} style={text.bodyStrong}>
                {entry.name}
              </Text>
              <View style={tw`flex-row items-center mt-1`}>
                <RolePill role={entry.role} />
                <Text numberOfLines={1} style={[text.hint, tw`ml-2 flex-1`]}>
                  {coverageText(entry)}
                </Text>
              </View>
            </View>
            {opening === entry.owner && (
              <ActivityIndicator
                size="small"
                color={colors.slate}
                style={tw`ml-2`}
              />
            )}
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
};

export default SharedWithYou;
